import { Component, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ModalDirective } from 'ngx-bootstrap/modal';
import { TypeaheadMatch } from 'ngx-bootstrap/typeahead/public_api';
import { Observable, of } from 'rxjs';
import { catchError, map, mergeMap, tap } from 'rxjs/operators';
import { InspectorService } from '../../inspector/inspector.service';
import { Inspection, Inspector, Vendor } from '../../shared/interfaces';
import { VendorService } from '../../vendor/vendor.service';
import { InspectionService } from '../inspection.service';

@Component({
  selector: 'app-assign-vendor-inspection',
  templateUrl: './assign.vendor.component.html',
  styleUrls: ['./assign.vendor.component.scss'],
})
export class InspectionAssignVendorComponent implements OnInit {
  @ViewChild('assignVendorModal', { static: true }) public assignVendorModal: ModalDirective;

  confirmCallback: () => void;
  cancelCallback: () => void;
  inspection: Inspection;

  assignVendorForm: UntypedFormGroup;

  vendorDataSource = of([]);
  selectedVendor: any;
  inspectorDataSource = of([]);
  selectedInspector: any;
  saving = false;
  vendors: Vendor[] = [];
  inspectors: Inspector[] = [];
  vendorLoading: boolean = false;
  inspectorLoading: boolean = false;
  allInspectorsLoaded: boolean = false;
  vendorInspectors: Inspector[] = [];
  viewMoreClicked: boolean = false;
  vendorViewMoreClicked: boolean = false;
  vendors$ = of([]);
  inspectors$ = of([]);
  inspectionProductTypeName: string;
  selectVendors: any = null;
  vendorMismatch: boolean = false;
  inspectorMismatch: boolean = false;

  constructor(
    public vendorService: VendorService,
    public inspectorService: InspectorService,
    public inspectionService: InspectionService,
    private fb: UntypedFormBuilder,
  ) {}

  ngOnInit() {
    this.vendorDataSource = new Observable(subscriber =>
      subscriber.next(this.assignVendorForm.controls.vendor.value),
    ).pipe(
      mergeMap((token: string) => this.vendorService.list({ search: token })),
      map(x => x[0]),
    );

    this.inspectorDataSource = new Observable(subscriber =>
      subscriber.next(this.assignVendorForm.controls.inspector.value),
    ).pipe(
      mergeMap((token: string) => {
        if (this.selectedVendor) {
          return this.vendorService.inspectors(this.selectedVendor.id, token);
        } else {
          return of([]);
        }
      }),
    );

    this.assignVendorForm = this.fb.group({
      vendor: ['', [Validators.required]],
      inspector: [{ value: '', disabled: true }],
    });

    this.assignVendorModal.onHidden.subscribe(x => {
      if (x.dismissReason === 'esc' || x.dismissReason === 'backdrop-click') {
        this.cancel();
      }
    });
  }

  typeaheadOnSelectVendor(ev: TypeaheadMatch) {
    if (this.selectedVendor && ev.item && this.selectedVendor.id !== ev.item.id) {
      this.selectedInspector = null;
      this.userBlurInspector();
    }
    this.selectedVendor = ev.item;
  }

  typeaheadOnSelectInspector(ev: TypeaheadMatch) {
    this.selectedInspector = ev.item;
  }

  userBlurVendor(ev: FocusEvent) {
    if (this.assignVendorForm.get('vendor').value === '') {
      this.selectedVendor = null;
      this.selectedInspector = null;
      this.userBlurInspector();
    } else {
      this.assignVendorForm.get('vendor').setValue(this.selectedVendor ? this.selectedVendor.name : '');
    }
  }
  userBlurInspector() {
    if (this.assignVendorForm.get('inspector').value === '') {
      this.selectedInspector = null;
    } else {
      this.assignVendorForm.get('inspector').setValue(this.selectedInspector ? this.selectedInspector.name : '');
    }
  }

  async show(options: { confirmed?: () => void; canceled?: () => void; inspection: Inspection }) {
    this.confirmCallback = options.confirmed;
    this.cancelCallback = options.canceled;
    this.inspection = options.inspection;

    this.inspectionProductTypeName = options?.inspection?.location?.policy?.productType?.name;
    await this.loadAllInspectors();
    await this.loadAllVendors();

    this.selectedVendor = this.inspection.vendor;
    this.assignVendorForm.controls.vendor.setValue(this.selectedVendor ? this.selectedVendor.name : '');
    this.assignVendorForm.controls.inspector.setValue(this.selectedInspector ? this.selectedInspector.name : '');
    this.assignVendorModal.show();
  }

  async save() {
    if (this.saving) return;
    if (this.assignVendorForm.valid) {
      this.saving = true;
      if (this.selectedInspector) {
        await this.inspectionService.assignInspector(this.inspection.id, this.selectedInspector);
      } else {
        await this.inspectionService.assignVendor(this.inspection.id, this.selectedVendor);
      }
      this.assignVendorModal.hide();
      this.saving = false;
      this.selectedVendor = null;
      this.selectedInspector = null;
      this.assignVendorForm.reset();
      return this.confirmCallback && this.confirmCallback();
    }
  }

  cancel() {
    this.assignVendorModal.hide();
    this.resetInspectors();
    this.clearVendor();
    return !!this.cancelCallback && this.cancelCallback();
  }

  getValidationClass(field: string) {
    const control = this.assignVendorForm.get(field);
    return {
      'is-valid': control.valid && (control.dirty || control.touched),
      'is-invalid': control.invalid && (control.dirty || control.touched),
    };
  }

  async loadAllVendors(loadFiltered: boolean = true) {
    this.vendorLoading = true;
    const filters: any = { isPagination: 0 };

    if (!this.inspectors || this.inspectors.length === 0) {
      await this.loadAllInspectors();
    }

    this.vendorService
      .list(filters)
      .pipe(
        tap(async (response: any) => {
          const data = response[0];
          if (Array.isArray(data)) {
            this.vendors = this.sortVendors(data);
          } else {
            this.vendors = this.sortVendors([data]);
          }

          this.vendorLoading = false;
          this.vendorViewMoreClicked = false;

          if (loadFiltered) {
            await this.filterVendorsByProductType();
          }

          if (!loadFiltered) {
            this.vendorViewMoreClicked = true;
          }
        }),
        catchError(error => {
          console.error('Error fetching vendors:', error);
          this.vendorLoading = false;
          this.vendors = [];
          this.vendors$ = of([]);
          return of([]);
        }),
      )
      .subscribe();
  }

  async filterVendorsByProductType() {
    if (
      !this.inspectionProductTypeName ||
      !this.vendors ||
      this.vendors.length === 0 ||
      !this.inspectors ||
      this.inspectors.length === 0
    ) {
      console.warn('Skipping vendor filtering: missing data.');
      return;
    }
    const filteredVendors = this.vendors.filter(vendor => {
      const vendorInspectors = this.inspectors.filter(inspector => inspector.vendor?.id === vendor.id);
      const hasMatchingInspector = vendorInspectors.some(inspector => {
        if (!inspector.productTypesConcat || typeof inspector.productTypesConcat !== 'string') {
          return false;
        }
        const productTypesArray = inspector.productTypesConcat.split(',').map(pt => pt.trim());
        return productTypesArray.includes(this.inspectionProductTypeName);
      });
      return hasMatchingInspector;
    });

    this.vendors = filteredVendors.sort((a, b) => a.name.localeCompare(b.name));
  }

  private sortVendors(vendors: any[]): any[] {
    return vendors.sort((a, b) => a.name.localeCompare(b.name));
  }

  vendorTrackByFn(vendor: Vendor) {
    return vendor.id;
  }

  async onVendorSelect(vendor: any) {
    this.vendorMismatch = false;
    if (typeof vendor === 'string') {
      this.selectedVendor = { name: vendor } as Vendor;
    } else if (typeof vendor === 'object' && vendor.id) {
      if (this.vendorViewMoreClicked && !this.vendorHasMatchingInspectors(vendor)) {
        this.selectVendors = vendor;
        this.vendorMismatch = true;
        this.selectVendor(vendor);
        this.assignVendorForm.get('inspector').enable();
      } else {
        this.selectVendor(vendor);
      }
    }
  }

  vendorHasMatchingInspectors(vendor: any): boolean {
    const vendorInspectors = this.inspectors.filter(inspector => inspector.vendor?.id === vendor.id);
    return vendorInspectors.some(inspector => {
      if (!inspector.productTypesConcat || typeof inspector.productTypesConcat !== 'string') {
        return false;
      }
      const productTypesArray = inspector.productTypesConcat.split(',').map(pt => pt.trim());
      return productTypesArray.includes(this.inspectionProductTypeName);
    });
  }

  async selectVendor(vendor: any) {
    this.selectedVendor = vendor;
    this.assignVendorForm.get('vendor').patchValue({
      id: vendor.id,
      name: vendor.name,
    });
    await this.loadVendorInspectors();
    this.assignVendorForm.get('inspector').enable();
  }

  async loadVendorInspectors(viewMore: boolean = false) {
    let vendorId = this.assignVendorForm.get('vendor').value;
    vendorId = vendorId?.id;
    if (!vendorId) {
      console.error('Vendor ID is not defined.');
      this.assignVendorForm.get('inspector').disable();
      return;
    }

    this.vendorInspectors = await this.vendorService
      .inspectors(vendorId, '', [this.inspection.coordinates[0], this.inspection.coordinates[1]].toString())
      .toPromise();

    if (viewMore) {
      this.inspectors = this.vendorInspectors;
      this.allInspectorsLoaded = true;
    } else {
      const inspectionProductTypeName = this.inspectionProductTypeName;

      if (!inspectionProductTypeName) {
        console.error('Inspection Product Type Name is not defined.');
        return;
      }

      this.inspectors = this.vendorInspectors.filter(inspector => {
        if (!inspector.productTypesConcat || typeof inspector.productTypesConcat !== 'string') {
          return false;
        }
        const productTypesArray = inspector.productTypesConcat.split(',').map(pt => pt.trim());
        return productTypesArray.includes(inspectionProductTypeName);
      });
      this.allInspectorsLoaded = false;
    }

    let inspectorId = this.assignVendorForm.get('inspector').value;
    inspectorId = inspectorId?.id;
    if (inspectorId) {
      const exists = this.inspectors.find(i => i.id === inspectorId);
      if (!exists) {
        this.assignVendorForm.get('inspector').setValue({
          id: null,
          name: '',
          email: '',
          phone: '',
        });
      }
    }
    this.assignVendorForm.get('inspector').enable();
  }

  async loadInspectors(vendorId: number) {
    this.inspectors = await this.vendorService.inspectors(vendorId, '').toPromise();
  }

  inspectorTrackByFn(inspector: Inspector) {
    return inspector.id;
  }

  onInspectorSelect(inspector: any) {
    this.inspectorMismatch = false;
    if (typeof inspector === 'string') {
      this.assignVendorForm.get('inspector').patchValue({ name: inspector });
    } else if (typeof inspector === 'object' && inspector.id) {
      if (!this.inspectorHasMatchingProductType(inspector)) {
        this.selectedInspector = inspector;
        this.selectInspector(inspector);
        this.inspectorMismatch = true;
        this.viewMoreClicked = true;
      } else {
        this.selectInspector(inspector);
      }
    }
  }

  inspectorHasMatchingProductType(inspector: any): boolean {
    if (!inspector.productTypesConcat || typeof inspector.productTypesConcat !== 'string') {
      return false;
    }
    const productTypesArray = inspector.productTypesConcat.split(',').map(pt => pt.trim());
    return productTypesArray.includes(this.inspectionProductTypeName);
  }

  selectInspector(inspector: any) {
    this.selectedInspector = inspector;

    this.assignVendorForm.get('inspector').patchValue({
      id: inspector.id,
      name: inspector.name,
      email: inspector.email,
      phone: inspector.phone,
    });
  }

  clearVendor() {
    this.vendorViewMoreClicked = false;
    this.selectedVendor = null;
    this.inspectors = [];
    this.selectedInspector = null;
    this.assignVendorForm.get('inspector').setValue({
      id: null,
    });
    this.assignVendorForm.get('inspector').disable();
    this.vendorMismatch = false;
    this.clearInspector();
  }

  async clearInspector() {
    this.assignVendorForm.get('inspector').setValue({
      id: null,
      name: '',
      email: '',
      phone: '',
    });
    this.selectedInspector = null;
    this.inspectorMismatch = false;
  }

  async loadAllInspectors() {
    this.inspectorLoading = true;
    const filters: any = { isPagination: 0 };
    this.inspectorService
      .list(filters)
      .pipe(
        tap((response: any) => {
          const data = response[0];
          if (Array.isArray(data)) {
            this.inspectors = data;
          } else {
            this.inspectors = [data];
          }

          this.inspectorLoading = false;
        }),
        catchError(error => {
          console.error('Error fetching inspectors:', error);
          this.inspectorLoading = false;
          this.inspectors = [];
          return of([]);
        }),
      )
      .subscribe();
  }

  viewMoreInspectors() {
    this.viewMoreClicked = true;
    this.loadVendorInspectors(true);
  }

  resetInspectors() {
    this.allInspectorsLoaded = false;
    this.viewMoreClicked = false;
  }
}
